import NotificationJoin from "@/components/tailwind/notification/notificationJoin";
import NotificationMention from "@/components/tailwind/notification/notificationMention";
import NotificationRequestAccepted from "@/components/tailwind/notification/notificationRequestAccepted";
import NotificationRequestRejected from "@/components/tailwind/notification/notificationRequestRejected";
import NotificationAssign from "@/components/tailwind/notification/notificationAssign";
import NotificationButton from "@/components/tailwind/notification/notificationButton";
import NotificationShare from "@/components/tailwind/notification/notificationShare";
import NotificationComment from "@/components/tailwind/notification/notificationComment";
import NotificationWorkspaceInvite from "@/components/tailwind/notification/notificationWorkspaceInvite";
import NotificationPermissionChanged from "@/components/tailwind/notification/notificationPermissionChanged";
import NotificationWorkAreaAccessGranted from "@/components/tailwind/notification/notificationWorkAreaAccessGranted";
import NotificationWorkAreaJoinRequest from "@/components/tailwind/notification/notificationWorkAreaJoinRequest";
import NotificationWorkAreaJoinRejected from "@/components/tailwind/notification/notificationWorkAreaJoinRejected";
import NotificationGroupChange from "@/components/tailwind/notification/notificationGroupChange";
import NotificationReminder from "@/components/tailwind/notification/notificationReminder";

import { CreatedBy, Notification } from "@/types/notification";
import { useWorkspaceContext } from "@/contexts/workspaceContext";

export default function NotificationRenderer({
  notification,
  handleAccept,
  handleReject,
  responded,
}: {
  notification: Notification;
  handleAccept: (id: string, sentTo: [CreatedBy], workspaceId: string) => void;
  handleReject: (id: string, sentTo: [CreatedBy], workspaceId: string) => void;
  responded: boolean;
}) {
  const { getMemberName } = useWorkspaceContext();
  // Live actor name (falls back to the stored name on the notification).
  const createdByName =
    getMemberName(notification.createdBy?.userId) ||
    getMemberName(notification.createdBy?.userEmail) ||
    notification.createdBy?.userName ||
    "Unknown User";

  switch (notification.type) {
    case "JOIN":
      return (
        <NotificationJoin
          notification={notification}
          handleAccept={handleAccept}
          handleReject={handleReject}
          responded={responded}
        />
      );

    case "MENTION":
      return (
        <NotificationMention
          key={notification._id}
          createdByName={createdByName}
          createdById={notification.createdBy.userId}
          createdByEmail={notification.createdBy.userEmail}
          noteTitle={notification.noteTitle}
          noteId={notification.noteId}
          workspaceName={notification.workspaceName}
          workspaceId={notification.workspaceId}
          createdAt={notification.createdAt}
          read={notification.read}
        />
      );

    case "ACCEPT":
      return (
        <NotificationRequestAccepted
          key={notification._id}
          createdByName={createdByName}
          createdById={notification.createdBy.userId}
          createdByEmail={notification.createdBy.userEmail}
          workspaceName={notification.workspaceName}
          workspaceId={notification.workspaceId}
          createdAt={notification.createdAt}
          read={notification.read}
        />
      );

    case "REJECT":
      return (
        <NotificationRequestRejected
          key={notification._id}
          createdByName={createdByName}
          createdById={notification.createdBy.userId}
          createdByEmail={notification.createdBy.userEmail}
          workspaceName={notification.workspaceName}
          workspaceId={notification.workspaceId}
          createdAt={notification.createdAt}
          read={notification.read}
        />
      );

    case "ASSIGN":
      return <NotificationAssign notification={notification} />;

    case "BUTTON":
      return <NotificationButton notification={notification} />;

    case "REMINDER":
      return <NotificationReminder notification={notification} />;

    case "SHARE":
      return <NotificationShare notification={notification} />;

    case "COMMENT":
      return <NotificationComment notification={notification} />;

    case "WORKSPACE_INVITE":
      return <NotificationWorkspaceInvite notification={notification} />;

    case "PERMISSION_CHANGED":
      return <NotificationPermissionChanged notification={notification} />;

    case "WORKAREA_ACCESS_GRANTED":
      return <NotificationWorkAreaAccessGranted notification={notification} />;

    case "WORKAREA_JOIN_REQUEST":
      return <NotificationWorkAreaJoinRequest notification={notification} />;

    case "WORKAREA_JOIN_REJECTED":
      return <NotificationWorkAreaJoinRejected notification={notification} />;

    case "GROUP_CREATED":
    case "GROUP_MEMBER_ADDED":
    case "GROUP_MEMBER_REMOVED":
    case "GROUP_RENAMED":
    case "GROUP_DELETED":
      return <NotificationGroupChange notification={notification} />;

    default:
      return (
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          {notification.message}
        </p>
      );
  }
}
